import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import {
  Upload,
  Refresh,
} from '@mui/icons-material';
import { marksAPI, examAPI, subjectAPI } from '../../services/api';

const MarksUpload = () => {
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [marks, setMarks] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const classes = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  useEffect(() => {
    loadExams();
  }, []);

  const loadExams = async () => {
    try {
      const response = await examAPI.getAll({ is_published: 1 });
      setExams(response.data);
    } catch (error) {
      console.error('Failed to load exams:', error);
    }
  };

  const loadSubjects = useCallback(async () => {
    try {
      const response = await subjectAPI.getByStandard(selectedClass);
      setSubjects(response.data);
    } catch (error) {
      console.error('Failed to load subjects:', error);
    }
  }, [selectedClass]);

  const loadMarks = useCallback(async () => {
    setLoading(true);
    setMessage(null);
    try {
      const response = await marksAPI.getMarks({
        exam_id: selectedExam,
        standard: selectedClass,
        subject_id: selectedSubject,
      });
      
      const studentsData = response.data;
      setStudents(studentsData);
      
      // Initialize marks state
      const initialMarks = {};
      const selectedSubjectData = subjects.find(s => String(s.id) === String(selectedSubject));
      const maxMarks = selectedSubjectData?.max_marks || 100;
      
      studentsData.forEach(student => {
        initialMarks[student.student_id] = {
          student_id: student.student_id,
          marks_obtained: student.marks_obtained || '',
          max_marks: student.max_marks || maxMarks,
          remarks: student.remarks || '',
        };
      });
      setMarks(initialMarks);
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to load marks: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setLoading(false);
    }
  }, [selectedClass, selectedExam, selectedSubject, subjects]);

  useEffect(() => {
    if (selectedClass) {
      loadSubjects();
    }
  }, [selectedClass, loadSubjects]);

  useEffect(() => {
    if (selectedClass && selectedExam && selectedSubject) {
      loadMarks();
    }
  }, [selectedClass, selectedExam, selectedSubject, loadMarks]);

  const handleMarksChange = (studentId, value) => {
    const numValue = parseFloat(value) || 0;
    const maxMarks = marks[studentId]?.max_marks || 100;
    
    if (numValue > maxMarks) {
      setMessage({
        type: 'warning',
        text: `Marks cannot exceed maximum marks (${maxMarks})`,
      });
      return;
    }
    
    setMarks(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        marks_obtained: value,
      },
    }));
  };

  const handleRemarksChange = (studentId, remarks) => {
    setMarks(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        remarks,
      },
    }));
  };

  const calculateGrade = (obtained, max) => {
    const percentage = (obtained / max) * 100;
    if (percentage >= 90) return 'A+';
    if (percentage >= 80) return 'A';
    if (percentage >= 70) return 'B+';
    if (percentage >= 60) return 'B';
    if (percentage >= 50) return 'C';
    if (percentage >= 35) return 'D';
    return 'F';
  };

  const handleSubmit = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const marksData = Object.values(marks).filter(m => m.marks_obtained !== '');
      
      await marksAPI.uploadMarks({
        exam_id: selectedExam,
        subject_id: selectedSubject,
        marks: marksData,
      });
      
      setMessage({
        type: 'success',
        text: `Marks uploaded successfully for ${marksData.length} students!`,
      });
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to save marks: ' + (error.response?.data?.message || error.message),
      });
    } finally {
      setSaving(false);
    }
  };

  const getGradeColor = (grade) => {
    if (['A+', 'A'].includes(grade)) return 'success';
    if (['B+', 'B'].includes(grade)) return 'info';
    if (['C'].includes(grade)) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Upload Marks
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Class/Standard</InputLabel>
              <Select
                value={selectedClass}
                label="Class/Standard"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                {classes.map((cls) => (
                  <MenuItem key={cls} value={cls}>
                    Class {cls}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!selectedClass}>
              <InputLabel>Examination</InputLabel>
              <Select
                value={selectedExam}
                label="Examination"
                onChange={(e) => setSelectedExam(e.target.value)}
              >
                {exams.map((exam) => (
                  <MenuItem key={exam.id} value={exam.id}>
                    {exam.exam_name} ({exam.exam_type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth disabled={!selectedClass}>
              <InputLabel>Subject</InputLabel>
              <Select
                value={selectedSubject}
                label="Subject"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.subject_name} (Max: {subject.max_marks})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {message && (
        <Alert severity={message.type} sx={{ mb: 3 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* Marks Entry Table */}
      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : students.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: 'primary.50' }}>
                  <TableCell>S.No</TableCell>
                  <TableCell>Enrollment No</TableCell>
                  <TableCell>Student Name</TableCell>
                  <TableCell>Marks Obtained</TableCell>
                  <TableCell>Max Marks</TableCell>
                  <TableCell>Percentage</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {students.map((student, index) => {
                  const studentMarks = marks[student.student_id] || {};
                  const obtained = parseFloat(studentMarks.marks_obtained) || 0;
                  const max = studentMarks.max_marks || 100;
                  const percentage = obtained > 0 ? ((obtained / max) * 100).toFixed(2) : 0;
                  const grade = obtained > 0 ? calculateGrade(obtained, max) : '-';

                  return (
                    <TableRow key={student.student_id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{student.enrollment_no}</TableCell>
                      <TableCell>{student.student_name}</TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          size="small"
                          value={studentMarks.marks_obtained || ''}
                          onChange={(e) => handleMarksChange(student.student_id, e.target.value)}
                          inputProps={{ min: 0, max: max, step: 0.5 }}
                          sx={{ width: 100 }}
                        />
                      </TableCell>
                      <TableCell>{max}</TableCell>
                      <TableCell>
                        <Typography fontWeight="bold">
                          {percentage}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {grade !== '-' && (
                          <Chip
                            label={grade}
                            color={getGradeColor(grade)}
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          placeholder="Add remarks..."
                          value={studentMarks.remarks || ''}
                          onChange={(e) => handleRemarksChange(student.student_id, e.target.value)}
                          fullWidth
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Summary and Actions */}
          <Paper sx={{ p: 3, mt: 3 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2}>
                  <Chip
                    label={`Total Students: ${students.length}`}
                    color="default"
                    variant="outlined"
                  />
                  <Chip
                    label={`Marks Entered: ${Object.values(marks).filter(m => m.marks_obtained !== '').length}`}
                    color="primary"
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box display="flex" gap={2} justifyContent="flex-end">
                  <Button
                    variant="outlined"
                    onClick={loadMarks}
                    startIcon={<Refresh />}
                  >
                    Reload
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={saving || students.length === 0}
                    startIcon={saving ? <CircularProgress size={20} /> : <Upload />}
                  >
                    {saving ? 'Uploading...' : 'Upload Marks'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </>
      ) : (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {selectedClass && selectedExam && selectedSubject
              ? 'No students found for this class'
              : 'Please select class, exam, and subject to upload marks'}
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default MarksUpload;
